/**
 * WRP-062 "Ask us to change it": what a customer asked the team to change in a
 * film they could not change themselves on my.echoflicks.com (its editor said
 * `frozen_off`, made while editing was off, or `no_refine_state`).
 *
 * Source: dashboard-api `GET /video-sessions/{id}/change-requests` (infra,
 * `services/dashboard-api/film_change_requests.py`): `{data: [...], total}`,
 * newest first. mobile-bff is the only writer. Read-only here: the team makes
 * the change with the audited operator re-cut (workspace
 * `docs/reprompt/runbook.md` section 3) and answers on WhatsApp.
 */

export type FilmChangeReason = "frozen_off" | "no_refine_state";

export type FilmChangeRequest = {
  id: string;
  message: string;
  createdAt: string;
  reason: FilmChangeReason | null;
  source: string | null;
};

export const FILM_CHANGE_REASON_LABELS: Record<FilmChangeReason, string> = {
  frozen_off: "Made while editing was off",
  no_refine_state: "No refine-state: the re-cut needs a backfill",
};

/** React Query prefix. Never persisted: the customer's own words. */
export const FILM_CHANGE_REQUESTS_QUERY_PREFIX = "film-change-requests";

export function filmChangeRequestsPath(sessionId: string | number) {
  return `/video-sessions/${encodeURIComponent(String(sessionId))}/change-requests`;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * The requests in the response, newest first. A row without an id, words or a
 * time is dropped rather than shown half; an unknown reason shows no badge.
 */
export function parseFilmChangeRequests(payload: unknown): FilmChangeRequest[] {
  const rows =
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : [];
  const requests: FilmChangeRequest[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    const id = text(record.id);
    const message = text(record.message);
    const createdAt = text(record.created_at);
    if (!id || !message || !createdAt) continue;
    const reason =
      record.reason === "frozen_off" || record.reason === "no_refine_state"
        ? record.reason
        : null;
    requests.push({
      id,
      message,
      createdAt,
      reason,
      source: text(record.source),
    });
  }
  return requests.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
